/**
 * @fileoverview Cells of one student row: the self/teacher assessment pair of a lesson and one deliverable
 * cell per activity (AR-924: rich tooltip + teacher-upload mark).
 */

import { useTranslation } from 'react-i18next'
import { AssessmentCell, DeliverableCell } from './TableCells'
import { DeliverableCellTooltip } from './DeliverableCellTooltip'
import { cellStyle } from './tableHelpers'
import type { CellMap, LessonAssessmentStatus } from './tableModel'
import type { TaskTrackActivity, TaskTrackLesson, TaskTrackStudentRow } from '../types'

interface LessonAssessmentCellsProps {
  readonly lesson: TaskTrackLesson
  readonly studentId: string
  readonly status: LessonAssessmentStatus
  readonly onTeacherAssessmentClick: (lessonId: string, studentId: string) => void
}

export function LessonAssessmentCells(props: LessonAssessmentCellsProps) {
  const { lesson, studentId, status, onTeacherAssessmentClick } = props
  const { t } = useTranslation()
  const label = (key: string) => t(`task_tracker.status.${key}`)
  return (
    <>
      <td style={cellStyle}>
        <AssessmentCell
          status={status.selfStatus}
          enabled
          lessonDate={lesson.date}
          tooltipCompleted={label('self_assessment_completed')}
          tooltipInProgress={label('self_assessment_in_progress')}
          tooltipPending={label('self_assessment_pending')}
        />
      </td>
      <td style={cellStyle}>
        <AssessmentCell
          status={status.teacherStatus}
          enabled
          lessonDate={lesson.date}
          tooltipCompleted={label('teacher_assessment_completed')}
          tooltipInProgress={label('teacher_assessment_in_progress')}
          tooltipPending={label('teacher_assessment_pending')}
          onClick={() => onTeacherAssessmentClick(lesson.id, studentId)}
        />
      </td>
    </>
  )
}

interface DeliverableCellTdProps {
  readonly activity: TaskTrackActivity
  readonly student: TaskTrackStudentRow
  readonly cellMap: CellMap
  readonly teacherStatus: LessonAssessmentStatus['teacherStatus']
  readonly currentUserId?: string
  readonly onClick: (activityId: string, studentId: string) => void
}

export function DeliverableCellTd(props: DeliverableCellTdProps) {
  const { activity, student, cellMap, teacherStatus, currentUserId, onClick } = props
  const cell = cellMap.get(activity.id)?.get(student.id)
  const completed = cell?.deliverableCompleted ?? false
  const tooltip = (
    <DeliverableCellTooltip
      cellDelivery={cell?.deliverable}
      completed={completed}
      studentFirstName={student.firstName}
      currentUserId={currentUserId}
      teacherAssessmentStatus={teacherStatus}
    />
  )
  return (
    <td style={cellStyle}>
      <DeliverableCell
        completed={completed}
        enabled
        uploadedByTeacher={Boolean(cell?.deliverable?.uploadedByTeacher)}
        tooltip={tooltip}
        onClick={() => onClick(activity.id, student.id)}
      />
    </td>
  )
}
